import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { CircularProgress, Box, Typography, Fab, Tooltip } from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import { slugToNavId } from '../utils/navIds';
import { resolveQuizSetForUnit } from '../services/quizService';
// Dynamische Quiz Seite (wird genutzt wenn kein statischer Quiz-Component existiert)
const DynamicQuizPage = React.lazy(() => import('./units/DynamicQuizPage'));

const unitContext = (require as any).context('../pages/units', false, /\.tsx$/);
const UNIT_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = unitContext
  .keys()
  .reduce((acc: Record<string, React.LazyExoticComponent<React.ComponentType<any>>>, key: string) => {
    const slug = key.replace('./', '').replace(/\.tsx$/, '');
    acc[slug] = React.lazy(() =>
      Promise.resolve(unitContext(key)).then((mod: any) => ({ default: mod.default ?? mod }))
    );
    return acc;
  }, {});

const UnitLoader: React.FC = () => {
  const { page } = useParams<{ page: string }>();
  const [err, setErr] = React.useState<string | null>(null);
  const [quizSet, setQuizSet] = React.useState<string | null>(null);
  const [quizLoading, setQuizLoading] = React.useState(false);
  const Comp = React.useMemo(() => {
    if (!page) return null;
    // Wenn statischer Component existiert: nutze ihn
    const found = UNIT_COMPONENTS[page];
    if (found) return found;
    // Dynamische Quiz Erkennung: Slug endet mit _quiz
    if (/_quiz$/i.test(page)) return DynamicQuizPage;
    return null;
  }, [page]);

  React.useEffect(() => {
  setErr(null);
    setQuizSet(null);
    setQuizLoading(false);
    if (!page) return;
    const unitId = slugToNavId(page);
    if (unitId) {
      let cancelled = false;
      setQuizLoading(true);
      resolveQuizSetForUnit(unitId)
        .then(setName => { if (!cancelled) setQuizSet(setName); })
        .catch(() => { if (!cancelled) setQuizSet(null); })
        .finally(() => { if (!cancelled) setQuizLoading(false); });
      return () => { cancelled = true; };
    }
  }, [page]);

  React.useEffect(() => {
    if (!page) return;
    if (!Comp) {
      console.warn('Unit konnte nicht geladen werden, slug unbekannt:', page);
      setErr(`Seite ${page} nicht gefunden`);
    } else {
      setErr(null);
    }
  }, [page, Comp]);

  if (err) return React.createElement(Typography, { color: 'error' }, err);
  if (!Comp) return React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', p: 4 } }, React.createElement(CircularProgress, null));

  return React.createElement(
    Box,
    { sx: { position: 'relative', flex: 1 } },
    React.createElement(
      React.Suspense,
      { fallback: React.createElement(CircularProgress, null) },
  React.createElement(Comp, { key: page })
    ),
    quizSet && React.createElement(
      Tooltip,
      {
        title: `Quiz öffnen: ${quizSet}`,
        children: React.createElement(
          RouterLink,
          { to: `/quizzes/set/${encodeURIComponent(quizSet)}/play`, style: { textDecoration: 'none' } },
          React.createElement(
            Fab,
            { color: 'secondary', size: 'medium', sx: { position: 'fixed', right: 92, bottom: 16, zIndex: 1200 }, disabled: quizLoading },
            React.createElement(QuizIcon, null)
          )
        )
      }
    )
  );
};

export default UnitLoader;
